import React, { useMemo, useState } from 'react';
import classNames from 'classnames';

import { FormattedMessage, useIntl } from '../../util/reactIntl';
import {
  DAYS_OF_WEEK,
  getStoreHoursStatus,
  hasEnabledStoreHours,
} from '../StoreHoursPage/StoreHoursForm/storeHoursUtils';

import css from './ListingPage.module.css';

/** Localized 12h time from `HH:mm` (store hours storage format). */
const formatTime24WithIntl = (intl, time24) => {
  if (!time24) return '';
  const [hRaw, mRaw] = time24.split(':');
  const h = parseInt(hRaw, 10);
  const m = parseInt(mRaw, 10);
  if (Number.isNaN(h) || Number.isNaN(m)) return time24;
  const d = new Date(2000, 0, 1, h, m, 0, 0);
  return intl.formatTime(d, { hour: 'numeric', minute: '2-digit', hour12: true });
};

const StoreHoursMaybe = ({ storeHours, storeTimeZone }) => {
  const intl = useIntl();
  const [isExpanded, setIsExpanded] = useState(false);

  const status = useMemo(() => getStoreHoursStatus(storeHours, undefined, storeTimeZone), [
    storeHours,
    storeTimeZone,
  ]);

  if (!hasEnabledStoreHours(storeHours)) {
    return null;
  }

  const todayKey = status.todayKey;

  const weekdayLabel = dayKey => {
    const day = DAYS_OF_WEEK.find(d => d.key === dayKey);
    return intl.formatMessage({
      id: `StorefrontTabContent.storeHours.weekday.${dayKey}`,
      defaultMessage: day?.label || dayKey,
    });
  };

  const todayHoursText = status.todayOpenSlots
    .map(
      slot =>
        `${formatTime24WithIntl(intl, slot.openTime)} - ${formatTime24WithIntl(
          intl,
          slot.closeTime
        )}`
    )
    .join(', ');

  const summaryMessage = status.isOpen ? (
    <FormattedMessage
      id="SectionMapMaybe.storeHours.status.todaySummary"
      defaultMessage="{weekday}, {hours}"
      values={{ weekday: weekdayLabel(todayKey), hours: todayHoursText }}
    />
  ) : status.nextOpenTime && status.nextOpenIsToday ? (
    <FormattedMessage
      id="SectionMapMaybe.storeHours.status.opensToday"
      defaultMessage="Opens today at {time}"
      values={{ time: formatTime24WithIntl(intl, status.nextOpenTime) }}
    />
  ) : status.nextOpenTime ? (
    <FormattedMessage
      id="SectionMapMaybe.storeHours.status.opensOnDay"
      defaultMessage="Opens {weekday} at {time}"
      values={{
        weekday: weekdayLabel(status.nextOpenDayKey),
        time: formatTime24WithIntl(intl, status.nextOpenTime),
      }}
    />
  ) : null;

  return (
    <div className={css.storeHoursSection}>
      <button
        type="button"
        className={css.storeHoursRow}
        onClick={() => setIsExpanded(prev => !prev)}
        aria-expanded={isExpanded}
        aria-controls="listing-page-store-hours-list"
      >
        <span className={css.storeHoursStatusBadge}>
          <span
            className={classNames(css.storeHoursStatusDot, {
              [css.storeHoursStatusOpen]: status.isOpen,
              [css.storeHoursStatusClosed]: !status.isOpen,
            })}
            aria-hidden="true"
          />
          <span
            className={classNames(css.storeHoursStatusText, {
              [css.storeHoursStatusOpen]: status.isOpen,
              [css.storeHoursStatusClosed]: !status.isOpen,
            })}
          >
            {status.isOpen ? (
              <FormattedMessage id="SectionMapMaybe.storeHours.status.open" defaultMessage="Open" />
            ) : (
              <FormattedMessage
                id="SectionMapMaybe.storeHours.status.closed"
                defaultMessage="Closed"
              />
            )}
          </span>
          {summaryMessage ? (
            <span className={css.storeHoursSummary}>
              &middot; {summaryMessage}
            </span>
          ) : null}
        </span>
        <span
          className={classNames(css.storeHoursChevron, {
            [css.storeHoursChevronExpanded]: isExpanded,
          })}
          aria-hidden
        >
          <i className="ri-arrow-down-s-line" />
        </span>
      </button>
      <div
        id="listing-page-store-hours-list"
        className={classNames(css.storeHoursExpandWrapper, {
          [css.storeHoursExpandWrapperOpen]: isExpanded,
        })}
      >
        <div className={css.storeHoursWeekList}>
          {DAYS_OF_WEEK.map(day => {
            const dayData = storeHours[day.key];
            const isEnabled = dayData?.enabled;
            const timeSlots = dayData?.timeSlots || [];
            const isToday = day.key === todayKey;

            return (
              <div key={day.key} className={css.storeHoursWeekRow}>
                <span className={css.storeHoursWeekDay}>
                  {intl.formatMessage({
                    id: `StorefrontTabContent.storeHours.weekday.${day.key}`,
                    defaultMessage: day.label,
                  })}
                </span>
                <div className={css.storeHoursWeekTimeContainer}>
                  {isEnabled && timeSlots.length > 0 ? (
                    timeSlots.map((timeSlot, index) => (
                      <span
                        key={index}
                        className={classNames(
                          css.storeHoursWeekTime,
                          isToday ? css.storeHoursWeekTimeToday : null
                        )}
                      >
                        {timeSlot?.openTime && timeSlot?.closeTime
                          ? `${formatTime24WithIntl(
                              intl,
                              timeSlot.openTime
                            )} - ${formatTime24WithIntl(intl, timeSlot.closeTime)}`
                          : intl.formatMessage({
                              id: 'StorefrontTabContent.storeHours.closed',
                              defaultMessage: 'Closed',
                            })}
                      </span>
                    ))
                  ) : (
                    <span
                      className={classNames(
                        css.storeHoursWeekTime,
                        isToday ? css.storeHoursWeekTimeToday : null
                      )}
                    >
                      <FormattedMessage
                        id="StorefrontTabContent.storeHours.closed"
                        defaultMessage="Closed"
                      />
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default StoreHoursMaybe;
